import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { GENRE_HIERARCHY } from '@/constants/genres';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { digitalOcean } from '@/integrations/digitalocean/client';
import { toast } from 'sonner';

const GenreSelector = ({ selectedGenres = [], onChange, className = "", disabled = false }) => {
  const [expandedParents, setExpandedParents] = useState({});
  const [isSuggestionDialogOpen, setIsSuggestionDialogOpen] = useState(false);
  const [genreName, setGenreName] = useState('');
  const [genreDescription, setGenreDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleParent = (parent) => {
    setExpandedParents(prev => ({
      ...prev,
      [parent]: !prev[parent]
    }));
  };

  const handleSubgenreToggle = (subgenre) => {
    const newSelection = selectedGenres.includes(subgenre)
      ? selectedGenres.filter(g => g !== subgenre)
      : [...selectedGenres, subgenre];
    onChange(newSelection);
  };

  const getSelectedCount = (parent) => {
    const subgenres = GENRE_HIERARCHY[parent];
    return subgenres.filter(sg => selectedGenres.includes(sg)).length;
  };

  const handleSuggestionSubmit = async (e) => {
    e.preventDefault();
    
    if (!genreName.trim()) {
      toast.error('Please enter a genre name');
      return;
    }

    setIsSubmitting(true);

    try {
      if (!session) {
        toast.error('You must be logged in to suggest a genre');
        setIsSubmitting(false);
        return;
      }

      const { error } = await digitalOcean
        .from('genre_suggestions')
        .insert({
          user_id: session.user.id,
          genre_name: genreName.trim(),
          description: genreDescription.trim() || null,
          status: 'pending'
        });

      if (error) throw error;

      toast.success("Genre suggestion submitted! We'll review it soon.");
      setGenreName('');
      setGenreDescription('');
      setIsSuggestionDialogOpen(false);
    } catch (error) {
      console.error('Error submitting genre suggestion:', error);

      const message = typeof error?.message === 'string' ? error.message.toLowerCase() : '';
      const errorMessage = error?.message || '';

      if (message.includes('permission denied') || message.includes('violates row-level security')) {
        toast.error('Permission denied submitting genre suggestion. Please try again later or contact support.');
      } else if (message.includes('relation') && message.includes('genre_suggestions')) {
        toast.error('Genre suggestions table not found. Please run migrations (digitalOcean db push) or contact support.');
      } else if (errorMessage.includes('column') && (errorMessage.includes('lyrics') || errorMessage.includes('song_story'))) {
        // This shouldn't happen with genre suggestions, but if it does, provide helpful guidance
        toast.error('Database schema out of date. Please run migrations (digitalOcean db push) or contact support.');
      } else {
        toast.error('Failed to submit genre suggestion: ' + (errorMessage || 'Unknown error'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`space-y-2 w-full overflow-x-hidden ${className}`}>
      {Object.entries(GENRE_HIERARCHY).map(([parent, subgenres]) => {
        const isExpanded = expandedParents[parent];
        const selectedCount = getSelectedCount(parent);

        return (
          <div key={parent} className="border border-border/20 rounded-lg overflow-hidden bg-background/5 w-full">
            {/* Parent Header */}
            <button
              onClick={() => !disabled ? toggleParent(parent) : null}
              disabled={disabled}
              className="w-full flex items-center justify-between p-2 md:p-4 hover:bg-background/10 transition-colors min-w-0 disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
            >
              <span className="font-['Rotten_Valley'] text-sm md:text-lg text-foreground truncate flex-1 min-w-0 pr-2">
                {parent}
                {selectedCount > 0 && (
                  <span className="ml-1 md:ml-2 text-primary whitespace-nowrap">({selectedCount} selected)</span>
                )}
              </span>
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground flex-shrink-0" />
              ) : (
                <ChevronDown className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground flex-shrink-0" />
              )}
            </button>

            {/* Subgenres */}
            {isExpanded && (
              <div className="p-2 md:p-4 pt-0 space-y-2 md:space-y-3">
                {subgenres.map(subgenre => (
                  <label
                    key={subgenre}
                    className={`flex items-center space-x-2 md:space-x-3 group min-w-0 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  >
                    <Checkbox
                      checked={selectedGenres.includes(subgenre)}
                      onCheckedChange={() => !disabled ? handleSubgenreToggle(subgenre) : null}
                      disabled={disabled}
                      className="border-border/40 flex-shrink-0"
                    />
                    <span className="font-['Rotten_Valley'] text-sm md:text-base text-foreground group-hover:text-primary transition-colors break-words min-w-0">
                      {subgenre}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {selectedGenres.length === 0 && (
        <p className="text-xs md:text-sm text-muted-foreground font-['Rotten_Valley'] mt-2 break-words">
          Select at least one genre to continue
        </p>
      )}

      {/* Genre Suggestion Button */}
      <div className="mt-4">
        <Button
          type="button"
          onClick={() => !disabled ? setIsSuggestionDialogOpen(true) : null}
          disabled={disabled}
          className="w-full neuro-base active:neuro-pressed rounded-xl py-2 md:py-3 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed"
          variant="outline"
        >
          <span className="text-[#a0a0a0] font-['Rotten_Valley']">
            Don't see your genre? Suggest one
          </span>
        </Button>
      </div>

      {/* Genre Suggestion Dialog */}
      <Dialog open={isSuggestionDialogOpen} onOpenChange={setIsSuggestionDialogOpen}>
        <DialogContent className="neuro-base border-[#1f1f1f] bg-[#141414] text-[#d0d0d0]">
          <DialogHeader>
            <DialogTitle className="text-[#d0d0d0] font-['Rotten_Valley'] text-xl">
              Suggest a Genre
            </DialogTitle>
            <DialogDescription className="text-[#808080] text-sm">
              Can't find the genre you're looking for? Suggest it and we'll consider adding it!
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSuggestionSubmit} className="space-y-4">
            <div>
              <label htmlFor="genre-name" className="block text-sm text-[#808080] mb-2">
                Genre Name *
              </label>
              <Input
                id="genre-name"
                type="text"
                value={genreName}
                onChange={(e) => setGenreName(e.target.value)}
                placeholder="e.g., Dark Jazz, Vaporwave, etc."
                className="neuro-pressed rounded-xl bg-transparent text-[#d0d0d0] placeholder:text-[#606060] focus:outline-none border-0"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="genre-description" className="block text-sm text-[#808080] mb-2">
                Description (optional)
              </label>
              <Textarea
                id="genre-description"
                value={genreDescription}
                onChange={(e) => setGenreDescription(e.target.value)}
                placeholder="Tell us about this genre..."
                className="neuro-pressed rounded-xl bg-transparent text-[#d0d0d0] placeholder:text-[#606060] focus:outline-none border-0 resize-none"
                rows={4}
                disabled={isSubmitting}
              />
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                onClick={() => {
                  setIsSuggestionDialogOpen(false);
                  setGenreName('');
                  setGenreDescription('');
                }}
                className="neuro-flat rounded-xl py-2 text-sm text-[#808080]"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="neuro-base active:neuro-pressed rounded-xl py-2 text-sm text-[#a0a0a0]"
                disabled={isSubmitting || !genreName.trim()}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Suggestion'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GenreSelector;
